import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { CATEGORY_LINKS } from '../catalogue/CatalogueNav';

const VARIANT_FIELDS = [
  ['code', 'Art. no.'], ['label', 'Version label'], ['finish', 'Finish / colour'], ['dimensions', 'Dimensions'],
  ['lightSource', 'Light source'], ['protectionClass', 'Protection class'], ['mounting', 'Mounting'],
  ['connection', 'Cable / canopy'], ['image', 'Image URL'],
];

const VariantsEditor = ({ variants, onChange }) => {
  const setField = (i, k, v) => onChange(variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="border-t pt-4" data-testid="admin-variants-editor">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900">Variants ({variants.length})</h4>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...variants, { code: '' }])} data-testid="admin-add-variant">
          <Plus className="h-4 w-4 mr-1" /> Add variant
        </Button>
      </div>
      <div className="space-y-3">
        {variants.map((v, i) => (
          <div key={i} className="border rounded-md p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 relative" data-testid={`admin-variant-${i}`}>
            {VARIANT_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input className="text-sm h-8" value={v[k] || ''} onChange={(e) => setField(i, k, e.target.value)} />
              </div>
            ))}
            <button type="button" onClick={() => onChange(variants.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-red-500" data-testid={`admin-remove-variant-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const toList = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

const ListTextarea = ({ value, format, parse, onChange, testId }) => {
  const [text, setText] = useState(format(value));
  useEffect(() => { setText(format(value)); }, [value, format]);
  return (
    <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(parse(text))} data-testid={testId} />
  );
};

const formatImages = (arr) => (arr || []).join('\n');
const formatDocs = (arr) => (arr || []).map((d) => `${d.name} | ${d.url}`).join('\n');
const parseDocs = (t) => toList(t).map((l) => {
  const [name, url] = l.split('|').map((x) => x.trim());
  return { name, url: url || name };
});

export const FamilyProductFields = ({ formData, setFormData }) => {
  const set = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }));
  const cats = formData.categories || [];
  const toggleCat = (slug) => set('categories', cats.includes(slug) ? cats.filter((c) => c !== slug) : [...cats, slug]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[['code', 'Model no.'], ['name', 'Name'], ['family', 'Family'], ['productType', 'Product type']].map(([k, label]) => (
          <div key={k} className="space-y-2">
            <Label>{label}</Label>
            <Input value={formData[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`admin-product-${k}`} />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Label>Categories</Label>
        <div className="flex flex-wrap gap-3" data-testid="admin-product-categories">
          {CATEGORY_LINKS.map((c) => (
            <label key={c.slug} className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={cats.includes(c.slug)} onChange={() => toggleCat(c.slug)} data-testid={`admin-category-${c.slug}`} />
              {c.label}
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description (UK English)</Label>
        <Textarea rows={6} value={formData.description || ''} onChange={(e) => set('description', e.target.value)} data-testid="admin-product-description" />
      </div>
      <div className="space-y-2">
        <Label>Main image URL</Label>
        <Input value={formData.mainImage || ''} onChange={(e) => set('mainImage', e.target.value)} data-testid="admin-product-mainimage" />
      </div>
      <div className="space-y-2">
        <Label>Gallery images (one URL per line)</Label>
        <ListTextarea value={formData.images} format={formatImages} parse={toList} onChange={(v) => set('images', v)} testId="admin-product-images" />
      </div>
      <div className="space-y-2">
        <Label>Downloads (one per line: Name | URL)</Label>
        <ListTextarea value={formData.documents} format={formatDocs} parse={parseDocs} onChange={(v) => set('documents', v)} testId="admin-product-documents" />
      </div>
      <VariantsEditor variants={formData.variants || []} onChange={(v) => set('variants', v)} />
    </>
  );
};
